import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Sparkles, Plus, LogOut, User, Shield } from 'lucide-react'
import { motion } from 'framer-motion'
import { Button, Card, CardContent, Input } from '@/components/ui'
import { useAuth } from '@/context/AuthContext'
import { useFamily } from '@/context/FamilyContext'
import { useToast } from '@/context/ToastContext'
import type { Child, AgeRange } from '@/types/database'

// Kid-friendly avatar options
const AVATARS = [
  { id: 'dragon', emoji: '🐉', label: 'Dragon' },
  { id: 'unicorn', emoji: '🦄', label: 'Unicorn' },
  { id: 'robot', emoji: '🤖', label: 'Robot' },
  { id: 'astronaut', emoji: '👨‍🚀', label: 'Astronaut' },
  { id: 'wizard', emoji: '🧙', label: 'Wizard' },
  { id: 'superhero', emoji: '🦸', label: 'Superhero' },
  { id: 'cat', emoji: '🐱', label: 'Cat' },
  { id: 'dog', emoji: '🐕', label: 'Dog' },
]

const AGE_RANGES: { value: AgeRange; label: string }[] = [
  { value: '6-8', label: '6-8 years' },
  { value: '9-11', label: '9-11 years' },
  { value: '12-14', label: '12-14 years' },
]

export function SelectWriter() {
  const navigate = useNavigate()
  const { signOut, user } = useAuth()
  const { children, addChild, setActiveWriter, family } = useFamily()
  const { showToast, celebrate } = useToast()

  const [isCreating, setIsCreating] = useState(false)
  const [newChildName, setNewChildName] = useState('')
  const [newChildAvatar, setNewChildAvatar] = useState('dragon')
  const [newChildAge, setNewChildAge] = useState<AgeRange>('9-11')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSelectWriter = (child: Child) => {
    setActiveWriter(child)
    celebrate(`Let's write, ${child.display_name}!`)
    navigate('/dashboard')
  }

  const handleCreateChild = async () => {
    if (!newChildName.trim()) {
      showToast('Please enter a name', 'error')
      return
    }

    setIsSubmitting(true)

    const child = await addChild({
      display_name: newChildName.trim(),
      avatar_id: newChildAvatar,
      age_range: newChildAge,
    })

    setIsSubmitting(false)

    if (child) {
      celebrate(`Welcome, ${child.display_name}!`)
      setIsCreating(false)
      setNewChildName('')
      setNewChildAvatar('dragon')
    }
  }

  const handleLogout = async () => {
    await signOut()
    navigate('/')
  }

  const getAvatarEmoji = (avatarId: string) => {
    return AVATARS.find((a) => a.id === avatarId)?.emoji || '👤'
  }

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)]">
      {/* Header */}
      <header className="p-6 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-[var(--color-kids-primary)]" />
          <span className="font-display text-2xl font-bold text-[var(--color-kids-text)]">
            Inkwell Jr.
          </span>
        </Link>

        <div className="flex items-center gap-4">
          <span className="text-sm text-[var(--color-kids-text-muted)]">
            {family?.parent_name || user?.email}
          </span>
          {user?.email === 'davehail@me.com' && (
            <Link to="/admin/waitlist">
              <Button variant="ghost" size="sm">
                <Shield className="w-4 h-4 mr-2" />
                Admin
              </Button>
            </Link>
          )}
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Log out
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto p-6">
        <div className="text-center mb-10">
          <h1 className="font-display text-[var(--font-size-kid-2xl)] font-bold text-[var(--color-kids-text)] mb-2">
            Who's Writing Today?
          </h1>
          <p className="text-[var(--color-kids-text-muted)]">
            Pick a writer to start your adventure
          </p>
        </div>

        {/* Children grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
          {children.map((child, index) => (
            <motion.div
              key={child.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card
                hoverable
                className="cursor-pointer"
                onClick={() => handleSelectWriter(child)}
              >
                <CardContent className="p-6 text-center">
                  <div className="text-5xl mb-3">{getAvatarEmoji(child.avatar_id)}</div>
                  <h3 className="font-display text-lg font-bold text-[var(--color-kids-text)] mb-1">
                    {child.display_name}
                  </h3>
                  <p className="text-sm text-[var(--color-kids-text-muted)]">
                    {child.total_words_written.toLocaleString()} words written
                  </p>
                  {child.current_streak_days > 0 && (
                    <p className="text-sm text-[var(--color-kids-secondary)] mt-1">
                      🔥 {child.current_streak_days} day streak
                    </p>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}

          {/* Add writer card */}
          {!isCreating && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: children.length * 0.1 }}
            >
              <Card
                hoverable
                className="cursor-pointer border-2 border-dashed border-[var(--color-kids-primary)]/30"
                onClick={() => setIsCreating(true)}
              >
                <CardContent className="p-6 text-center h-full flex flex-col items-center justify-center min-h-[160px]">
                  <div className="w-12 h-12 rounded-full bg-[var(--color-kids-primary)]/10 flex items-center justify-center mb-3">
                    <Plus className="w-6 h-6 text-[var(--color-kids-primary)]" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-[var(--color-kids-primary)]">
                    Add Writer
                  </h3>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>

        {/* Create child form */}
        {isCreating && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="max-w-md mx-auto">
              <CardContent className="p-6">
                <h2 className="font-display text-xl font-bold text-[var(--color-kids-text)] mb-6 text-center">
                  Add a New Writer
                </h2>

                <div className="space-y-6">
                  <Input
                    label="Writer's Name"
                    placeholder="e.g., Emma"
                    value={newChildName}
                    onChange={(e) => setNewChildName(e.target.value)}
                    autoFocus
                  />

                  {/* Avatar picker */}
                  <div>
                    <label className="block text-[var(--font-size-kid-base)] font-semibold text-[var(--color-kids-text)] mb-3">
                      Pick an Avatar
                    </label>
                    <div className="grid grid-cols-4 gap-3">
                      {AVATARS.map((avatar) => (
                        <button
                          key={avatar.id}
                          type="button"
                          onClick={() => setNewChildAvatar(avatar.id)}
                          className={`
                            p-3 rounded-[var(--radius-kid)] text-3xl
                            transition-all duration-200
                            ${
                              newChildAvatar === avatar.id
                                ? 'bg-[var(--color-kids-primary)] ring-4 ring-[var(--color-kids-primary)]/30 scale-110'
                                : 'bg-[var(--color-kids-surface-alt)] hover:bg-[var(--color-kids-primary)]/10'
                            }
                          `}
                        >
                          {avatar.emoji}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Age range */}
                  <div>
                    <label className="block text-[var(--font-size-kid-base)] font-semibold text-[var(--color-kids-text)] mb-3">
                      Age Range
                    </label>
                    <div className="flex gap-3">
                      {AGE_RANGES.map((age) => (
                        <button
                          key={age.value}
                          type="button"
                          onClick={() => setNewChildAge(age.value)}
                          className={`
                            flex-1 py-3 px-4 rounded-[var(--radius-kid)] font-semibold
                            transition-all duration-200
                            ${
                              newChildAge === age.value
                                ? 'bg-[var(--color-kids-primary)] text-white'
                                : 'bg-[var(--color-kids-surface-alt)] text-[var(--color-kids-text)] hover:bg-[var(--color-kids-primary)]/10'
                            }
                          `}
                        >
                          {age.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="ghost"
                      className="flex-1"
                      onClick={() => {
                        setIsCreating(false)
                        setNewChildName('')
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={handleCreateChild}
                      isLoading={isSubmitting}
                    >
                      Add Writer
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Empty state */}
        {children.length === 0 && !isCreating && (
          <div className="text-center py-12">
            <User className="w-16 h-16 text-[var(--color-kids-text-light)] mx-auto mb-4" />
            <h2 className="font-display text-xl font-bold text-[var(--color-kids-text)] mb-2">
              No Writers Yet
            </h2>
            <p className="text-[var(--color-kids-text-muted)] mb-6">
              Add your first writer to get started!
            </p>
            <Button onClick={() => setIsCreating(true)}>
              <Plus className="w-5 h-5 mr-2" />
              Add Your First Writer
            </Button>
          </div>
        )}
      </main>
    </div>
  )
}
